import PlacesAutocomplete from '@c/forms/controls/PlacesAutocomplete';
import PageSection from '@c/listing/shared/PageSection';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useLoadScript } from '@react-google-maps/api';
import { useCreateListing } from '../_context';
const libraries = ['places'] as ('places' | 'drawing' | 'geometry')[];

export default function BikeLocation() {
  const { form } = useCreateListing();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  const handleSelect = ({
    lat,
    lng,
    address,
  }: {
    lat?: number;
    lng?: number;
    address: string;
  }) => {
    const hasLatLng = lat && lng;
    if (hasLatLng) {
      form.setValue('location', {
        longitude: +lng.toFixed(2),
        latitude: +lat.toFixed(2),
      });
    }
    form.setValue('location_display', address);
  };

  return (
    <PageSection title="Bike Location" required>
      <div className="mt-16 flex w-full flex-col items-start justify-between gap-x-16 sm:flex-row">
        <div className="flex flex-col items-start gap-y-6">
          <div className="flex w-full items-center justify-between rounded-[2rem] bg-brand-lightest-gray p-4 sm:w-[90rem]">
            {isLoaded ? (
              <PlacesAutocomplete
                requireLatLng
                setSelected={handleSelect}
                className="bg-inherit px-0 !text-[1.8rem]"
                hideSearchIcon
                MXAuto={false}
              />
            ) : null}
            <MagnifyingGlassIcon className="mr-6 h-8 w-8 text-black" />
          </div>
          <div className="flex items-center gap-x-4">
            <span className="text-[1.8rem] text-zinc-600">Powered by</span>
            <GoogleIcon />
          </div>
        </div>
        <div className="flex flex-col gap-y-6">
          <h3 className="text-[1.8rem] font-semibold text-black">
            Exact location is never used on MX Locker.
          </h3>
          <p className="text-[1.5rem] text-zinc-600">
            As privacy and safety is one of our top priorities, we use your
            location to give buyers an approximate idea of where your bike is
            located.
          </p>
        </div>
      </div>
    </PageSection>
  );
}

const GoogleIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="61"
      height="20"
      viewBox="0 0 61 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_11178_105693)">
        <path
          d="M26.0185 10.5247C26.0185 13.4008 23.7685 15.5201 21.0073 15.5201C18.2461 15.5201 15.9961 13.4008 15.9961 10.5247C15.9961 7.62836 18.2461 5.5293 21.0073 5.5293C23.7685 5.5293 26.0185 7.62836 26.0185 10.5247ZM23.8248 10.5247C23.8248 8.72744 22.5208 7.49773 21.0073 7.49773C19.4938 7.49773 18.1898 8.72744 18.1898 10.5247C18.1898 12.304 19.4938 13.5517 21.0073 13.5517C22.5208 13.5517 23.8248 12.3017 23.8248 10.5247Z"
          fill="#EA4335"
        />
        <path
          d="M36.831 10.5247C36.831 13.4008 34.581 15.5201 31.8198 15.5201C29.0586 15.5201 26.8086 13.4008 26.8086 10.5247C26.8086 7.63061 29.0586 5.5293 31.8198 5.5293C34.581 5.5293 36.831 7.62836 36.831 10.5247ZM34.6373 10.5247C34.6373 8.72744 33.3333 7.49773 31.8198 7.49773C30.3063 7.49773 29.0023 8.72744 29.0023 10.5247C29.0023 12.304 30.3063 13.5517 31.8198 13.5517C33.3333 13.5517 34.6373 12.3017 34.6373 10.5247Z"
          fill="#FBBC05"
        />
        <path
          d="M47.1897 5.83109V14.7994C47.1897 18.4885 45.0141 19.9953 42.442 19.9953C40.0209 19.9953 38.5637 18.3759 38.0142 17.0516L39.9241 16.2566C40.2641 17.0697 41.0975 18.0291 42.4398 18.0291C44.0862 18.0291 45.1064 17.0133 45.1064 15.1012V14.3828H45.0298C44.5388 14.9886 43.5929 15.5179 42.3992 15.5179C39.9015 15.5179 37.6133 13.3422 37.6133 10.5427C37.6133 7.72295 39.9015 5.5293 42.3992 5.5293C43.5907 5.5293 44.5366 6.05857 45.0298 6.6464H45.1064V5.83335H47.1897V5.83109ZM45.2618 10.5427C45.2618 8.78375 44.0884 7.49773 42.5952 7.49773C41.0817 7.49773 39.8137 8.78375 39.8137 10.5427C39.8137 12.2837 41.0817 13.5517 42.5952 13.5517C44.0884 13.5517 45.2618 12.2837 45.2618 10.5427Z"
          fill="#4285F4"
        />
        <path
          d="M50.624 0.574219V15.2136H48.4844V0.574219H50.624Z"
          fill="#34A853"
        />
        <path
          d="M58.9634 12.1691L60.6661 13.3042C60.1166 14.1173 58.7923 15.5182 56.504 15.5182C53.6662 15.5182 51.5469 13.3245 51.5469 10.5228C51.5469 7.55209 53.6842 5.52734 56.2585 5.52734C58.8508 5.52734 60.1188 7.59037 60.5332 8.70522L60.7607 9.27278L54.0829 12.0385C54.5941 13.0407 55.3892 13.552 56.504 13.552C57.6211 13.552 58.3959 13.0024 58.9634 12.1691ZM53.7225 10.3719L58.1864 8.51829C57.9409 7.89442 57.2022 7.45974 56.3328 7.45974C55.218 7.45974 53.6662 8.44396 53.7225 10.3719Z"
          fill="#EA4335"
        />
        <path
          d="M7.89824 9.22448V7.10514H15.04C15.1098 7.47451 15.1459 7.91144 15.1459 8.3844C15.1459 9.97446 14.7112 11.9406 13.3103 13.3415C11.9477 14.7604 10.2068 15.5172 7.90049 15.5172C3.62579 15.5172 0.03125 12.0352 0.03125 7.76054C0.03125 3.48583 3.62579 0.00390625 7.90049 0.00390625C10.2653 0.00390625 11.95 0.931819 13.2157 2.14126L11.7203 3.63673C10.8126 2.78539 9.5829 2.12324 7.89824 2.12324C4.77667 2.12324 2.33527 4.63897 2.33527 7.76054C2.33527 10.8821 4.77667 13.3978 7.89824 13.3978C9.92298 13.3978 11.0761 12.5848 11.8148 11.8461C12.4139 11.247 12.8081 10.3911 12.9635 9.22223L7.89824 9.22448Z"
          fill="#4285F4"
        />
      </g>
      <defs>
        <clipPath id="clip0_11178_105693">
          <rect width="60.7895" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
